import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useTable, useSortBy, useFilters, usePagination } from "react-table";
import axios from "axios";
import Table from "../components/tableComponent";

const compatibilityOptions = [
  "GIA-22",
  "SUP-22",
  "RIE-22",
  "GIA-23",
  "WHI-23",
  "GIA-24",
  "MER-24",
];

const ServiceParts = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterInput, setFilterInput] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  // Fetch parts list – reused after a sync
  const fetchServiceParts = useCallback(async () => {
    try {
      const response = await axios.get("/api/service-parts");
      setData(response.data || []);
    } catch (error) {
      console.error("Error fetching service parts:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchServiceParts();
  }, [fetchServiceParts]);

  /**
   * Trigger Rackbeat sync and immediately surface the result counts
   */
  const handleRackbeatSync = async () => {
    setSyncing(true);
    try {
      // POST returns an object like { inserted, updated, skipped }
      const { data: result } = await axios.post("/api/service-parts/sync");
      setSyncResult(result);

      // Refresh the table with any new/updated items
      await fetchServiceParts();

      // Auto‑dismiss the banner after a few seconds
      setTimeout(() => setSyncResult(null), 8000);
    } catch (error) {
      console.error("Error triggering Rackbeat sync:", error);
      alert("Failed to trigger Rackbeat sync");
    } finally {
      setSyncing(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilterInput(e.target.value);
    if (filterCategory) {
      setFilter(filterCategory, e.target.value);
    }
  };

  const handleUpdateModelCompatibility = useCallback(
    async (id, selectedModels) => {
      try {
        await axios.put(`/api/service-parts/${id}/model-compatibility`, {
          modelCompatibility: selectedModels,
        });
      } catch (error) {
        console.error("Error updating model compatibility:", error);
      }
    },
    []
  );

  const handleDeletePart = useCallback(async (id) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to delete this part?"
    );
    if (!isConfirmed) return;

    try {
      const response = await axios.delete(`/api/service-parts/${id}`);
      if (response.status === 200) {
        setData((prev) => prev.filter((item) => item._id !== id));
      }
    } catch (error) {
      console.error("Error deleting service part:", error);
    }
  }, []);

  const columns = useMemo(
    () => [
      { Header: "Product No", accessor: "productNo" },
      { Header: "Product Name", accessor: "productName" },
      { Header: "Product Description", accessor: "productDescription" },
      { Header: "Purchase Cost (Ex VAT)", accessor: "purchaseCostExVat" },
      { Header: "Sales Price (Ex VAT)", accessor: "salesPriceExVat" },
      { Header: "Sales Price (Inc VAT)", accessor: "salesPriceIncVat" },
      { Header: "MRSP (Ex VAT)", accessor: "MRSPExVat" },
      { Header: "MRSP (Inc VAT)", accessor: "MRSPIncVat" },
      { Header: "Group Code", accessor: "groupCode" },
      { Header: "Group Name", accessor: "groupName" },
      {
        Header: "Model Compatibility",
        accessor: "modelCompatibility",
        Cell: ({ row }) => {
          const [isDropdownOpen, setIsDropdownOpen] = useState(false);
          const [localSelectedModels, setLocalSelectedModels] = useState(
            row.original.modelCompatibility || []
          );
          const [isUpdating, setIsUpdating] = useState(false);

          const handleCheckboxChange = (option) => {
            setLocalSelectedModels((cur) =>
              cur.includes(option)
                ? cur.filter((m) => m !== option)
                : [...cur, option]
            );
          };

          const handleUpdateClick = async () => {
            setIsUpdating(true);
            await handleUpdateModelCompatibility(
              row.original._id,
              localSelectedModels
            );
            setData((prev) =>
              prev.map((item) =>
                item._id === row.original._id
                  ? { ...item, modelCompatibility: localSelectedModels }
                  : item
              )
            );
            setIsUpdating(false);
            setIsDropdownOpen(false);
          };

          return (
            <div className="relative">
              <button
                className="bg-joule text-white px-3 py-1 rounded-lg"
                onClick={() => setIsDropdownOpen((prev) => !prev)}
              >
                Edit Compatibility
              </button>
              {isDropdownOpen && (
                <div className="absolute mt-2 bg-white border border-gray-300 rounded-lg shadow-lg p-6 w-60 z-10 text-xl">
                  {compatibilityOptions.map((option) => (
                    <label
                      key={option}
                      className="flex items-center py-2 text-lg space-x-3"
                    >
                      <input
                        type="checkbox"
                        className="form-checkbox accent-joule rounded-full h-6 w-6 cursor-pointer"
                        checked={localSelectedModels.includes(option)}
                        onChange={() => handleCheckboxChange(option)}
                      />
                      <span>{option}</span>
                    </label>
                  ))}
                  <button
                    onClick={handleUpdateClick}
                    disabled={isUpdating}
                    className={`mt-4 py-2 px-4 rounded-lg w-full ${
                      isUpdating
                        ? "bg-gray-400 cursor-wait text-white"
                        : "bg-joule text-white cursor-pointer hover:bg-joule-dark"
                    }`}
                  >
                    {isUpdating ? "Updating..." : "Update"}
                  </button>
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: "Actions",
        id: "actions",
        Cell: ({ row }) => (
          <button
            onClick={() => handleDeletePart(row.original._id)}
            className="text-red-500 hover:text-red-700"
          >
            ❌
          </button>
        ),
      },
    ],
    [handleDeletePart, handleUpdateModelCompatibility]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    previousPage,
    nextPage,
    setFilter,
    rows,
    state: { pageIndex, pageSize },
    setPageSize,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
      autoResetPage: false,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  // After the useTable(...) destructuring:
  const prepareRowWithColor = useCallback(
    (row) => {
      // 1️⃣ Let react-table do its normal work
      prepareRow(row);

      // 2️⃣ Decide if this row should be red
      const lacksCompatibility =
        !row.original.modelCompatibility ||
        row.original.modelCompatibility.length === 0;

      if (!lacksCompatibility) {
        // Nothing to add
        return;
      }

      // 3️⃣ Patch getRowProps so the class survives
      const baseGetRowProps = row.getRowProps;
      row.getRowProps = (userProps = {}) => {
        const base = baseGetRowProps(userProps);
        return {
          ...base,
          className: [
            base.className, // whatever was already there
            "bg-red-100", // our highlight
          ]
            .filter(Boolean)
            .join(" "),
        };
      };
    },
    [prepareRow]
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex-grow p-4">
      <h2 className="text-2xl font-semibold mb-4">Service Parts</h2>

      {/* Optional success banner */}
      {syncResult && (
        <div className="mb-6 flex items-center justify-between bg-joule/10 border-l-4 border-joule text-joule-dark p-4 rounded-lg shadow">
          <span>
            Sync complete: <strong>{syncResult.inserted}</strong> inserted,{" "}
            <strong>{syncResult.updated}</strong> updated,{" "}
            <strong>{syncResult.skipped}</strong> skipped.
          </span>
          <button onClick={() => setSyncResult(null)} className="font-bold">
            ✕
          </button>
        </div>
      )}

      {/* Filters Row */}
      <div className="flex justify-between items-center mb-8">
        <div className="flex space-x-2">
          {/* Filter Category Selection */}
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="border p-2 rounded focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="">Select Filter Category...</option>
            {columns.map((column) => (
              <option value={column.accessor} key={column.accessor}>
                {column.Header}
              </option>
            ))}
          </select>

          {/* Text input for entering filter values */}
          <input
            value={filterInput}
            onChange={handleFilterChange}
            placeholder="Enter filter value..."
            className="border p-2 rounded w-full focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>

        {/* Rackbeat Sync Button */}
        <button
          onClick={handleRackbeatSync}
          disabled={syncing}
          className={`bg-joule text-white px-4 py-2 rounded-lg ml-4 ${
            syncing
              ? "opacity-50 cursor-wait"
              : "hover:bg-joule-dark cursor-pointer"
          }`}
        >
          {syncing ? "Syncing..." : "Sync with Rackbeat"}
        </button>
      </div>

      <Table
        columns={columns}
        data={page}
        getTableProps={getTableProps}
        getTableBodyProps={getTableBodyProps}
        headerGroups={headerGroups}
        prepareRow={prepareRowWithColor}
        canPreviousPage={canPreviousPage}
        canNextPage={canNextPage}
        previousPage={previousPage}
        nextPage={nextPage}
        pageSize={pageSize}
        pageIndex={pageIndex}
        setPageSize={setPageSize}
        totalFilteredRows={rows.length}
      />
    </div>
  );
};

export default ServiceParts;
